// Spec 007-vehicle-transfer-flow — useTransferStatus.
//
// Firestore listener for the outgoing owner's view of pending transfers on a
// vehicle. Subscribes to `vehicles/{vehicleId}/transfers` constrained to
// `outgoing_uid == auth.uid AND status == 'pending'`, limit 1.
//
// The query MUST carry the `outgoing_uid` equality: firestore.rules gates
// reads on `request.auth.uid == resource.data.outgoing_uid`, and Firestore
// proves list queries against the QUERY, not per-document — an unconstrained
// orderBy/limit subscription is PERMISSION_DENIED outright. (Found when the
// transfer e2e specs were re-enabled against the rules-enforcing emulator,
// gg-web #76; the original orderBy-only query had never run against real
// rules.) Both filters are equalities, so no composite index is needed.
//
// The `status == 'pending'` filter also implements FR-015's display contract
// directly: cancelled/declined/expired/completed docs simply never match, so
// the consumer sees "no pending transfer" without client-side collapsing.
//
// The incoming owner does NOT use this hook — they have no client-side read
// access to the source vehicle's subcollection (firestore.rules forbids).
// They consume the gg-app /v1/transfer/validate endpoint instead.

import { useAuth } from '@/features/auth/useAuth';
import type { TransferDoc } from '@/types/domain';
import { useCallback } from 'react';
import { useFirestoreCollection, type FirestoreConstraintsBuilder } from './useFirestoreCollection';

export interface TransferStatusResult {
  /** The vehicle's live pending transfer initiated by this user, or null. */
  transfer: TransferDoc | null;
  /** True iff `transfer` exists and `transfer.status === 'pending'`. */
  isPending: boolean;
  loading: boolean;
  error: Error | null;
}

export function useTransferStatus(vehicleId: string | null | undefined): TransferStatusResult {
  const { user } = useAuth();
  const uid = user?.uid ?? null;
  const buildConstraints = useCallback<FirestoreConstraintsBuilder>(
    (sdk) => [
      sdk.where('outgoing_uid', '==', uid ?? ''),
      sdk.where('status', '==', 'pending'),
      sdk.limit(1),
    ],
    [uid],
  );
  const path = vehicleId && uid ? `vehicles/${vehicleId}/transfers` : null;
  const { data, loading, error } = useFirestoreCollection<TransferDoc>(path, buildConstraints);
  const transfer = data[0] ?? null;
  const isPending = transfer?.status === 'pending';
  return { transfer, isPending, loading, error };
}
